import { AnalyticsContext } from '@carvana/eventable-clickstream';
import { datadogRum } from '@datadog/browser-rum';
import { NavigateToWindowWithNativeEvents, LinkWithNativeEvents } from '@components/_common/link/link';
import React, { useContext, useState } from 'react';
import { useIntersectionObserver } from 'usehooks-ts';
import styled from 'styled-components';
import { GapCoverageIcon } from '../../assets/GapCoverageIcon';

const StyledLinkWithNativeEvents = styled(LinkWithNativeEvents)`
  @media (max-width: 690px) {
    display: block;
  }
`;
import { TrackGapCoverageCardClickedProps } from '../../types';
import {
  GapCoverageAccessClaimLink,
  GapCoverageButton,
  GapCoverageCardContentContainer,
  GapCoverageCardHeader,
  GapCoverageCardHeaderContainer,
  GapCoverageCardIconContainer,
  GapCoverageCardListItemContainer,
  GapCoverageCardWrapper,
  GapCoverageClaimLinksContainer,
  GapCoverageInfo,
  GapCoverageInfoBody,
  GapCoverageSubHeading,
  GapCoverageSubHeadingContainer,
  GapCoverageSubText,
  GapCoverageTelephoneLink,
} from './GapCoverageCard.styles';
import { AnalyticEvents } from '../../constants';

const GapCoverageCard = ({ feedData, positionValue }: any) => {
  const { trackEvent } = useContext(AnalyticsContext);

  const [cardPosition] = useState(positionValue || 0);

  const { id: cardId, title, callToAction } = feedData;

  const trackGapCoverageCardClicked = ({ ctaClicked, contactAllyClicked }: TrackGapCoverageCardClickedProps) => {
    trackEvent(AnalyticEvents.ovpCardClicked, {
      cardId,
      cardPosition,
      ctaClicked,
      contactAllyClicked,
    });
    datadogRum.addAction(AnalyticEvents.ovpCardClicked, {
      cardId,
      cardPosition,
      ctaClicked,
      contactAllyClicked,
    });
  };

  const handleRedirect = ({ ctaClicked, contactAllyClicked }: TrackGapCoverageCardClickedProps) => {
    trackGapCoverageCardClicked({ ctaClicked, contactAllyClicked });
    NavigateToWindowWithNativeEvents({
      url: callToAction.link,
      event: 'link',
    });
  };

  const handleOnShow = () => {
    setTimeout(() => {
      trackEvent(AnalyticEvents.viewedOvpCardImpression, {
        // TODO: move this to config when the API work is completed
        cardId: 18,
        cardPosition: positionValue,
      });
      datadogRum.addAction(AnalyticEvents.viewedOvpCardImpression, {
        cardId: 18,
        cardPosition: positionValue,
      });
    }, 1000);
  };
  const { ref } = useIntersectionObserver({
    freezeOnceVisible: true,
    threshold: 0.5,
    onChange: (isIntersecting) => {
      if (isIntersecting && handleOnShow) handleOnShow();
    },
  });

  return (
    <GapCoverageCardWrapper ref={ref}>
      <GapCoverageCardHeaderContainer>
        <GapCoverageCardIconContainer>
          <GapCoverageIcon />
        </GapCoverageCardIconContainer>
        <GapCoverageCardHeader data-testid="gap-coverage-card-title">{title}</GapCoverageCardHeader>
      </GapCoverageCardHeaderContainer>
      <GapCoverageCardContentContainer>
        <GapCoverageInfo>
          <GapCoverageInfoBody>
            <GapCoverageSubHeadingContainer>
              <GapCoverageSubHeading data-testid="gap-coverage-card-sub-heading">
                You&apos;re protected in the event of a total vehicle loss or theft.
              </GapCoverageSubHeading>
              <GapCoverageSubText data-testid="gap-coverage-card-sub-title">
                Gap Coverage covers the remaining amount owed after insurance payout.
              </GapCoverageSubText>
            </GapCoverageSubHeadingContainer>
            <GapCoverageClaimLinksContainer>
              <GapCoverageCardListItemContainer>
                <ul>
                  <li data-testid="gap-coverage-card-list-item-1">
                    Questions? Contact Ally at{' '}
                    <StyledLinkWithNativeEvents
                      href="tel:+18662121304"
                      nativeEvent={{
                        event: 'link',
                        payload: { url: 'tel:+18662121304' },
                      }}
                      onClick={() =>
                        trackGapCoverageCardClicked({
                          ctaClicked: false,
                          contactAllyClicked: true,
                        })
                      }
                    >
                      1-866-212-1304.
                    </StyledLinkWithNativeEvents>
                  </li>
                  <li data-testid="gap-coverage-card-list-item-2">
                    <div>Already reported an incident?</div>
                    <GapCoverageAccessClaimLink
                      data-testid="gap-coverage-access-claim-link"
                      onClick={() =>
                        handleRedirect({
                          ctaClicked: false,
                          contactAllyClicked: false,
                        })
                      }
                    >
                      Access Your Claim
                    </GapCoverageAccessClaimLink>
                  </li>
                </ul>
              </GapCoverageCardListItemContainer>
            </GapCoverageClaimLinksContainer>
          </GapCoverageInfoBody>
          <GapCoverageButton
            data-testid="gap-coverage-card-cta-button"
            size="small"
            onClick={() => handleRedirect({ ctaClicked: true, contactAllyClicked: false })}
          >
            {callToAction.title}
          </GapCoverageButton>
        </GapCoverageInfo>
      </GapCoverageCardContentContainer>
    </GapCoverageCardWrapper>
  );
};

export default GapCoverageCard;
